import { t9n } from 't9n/t9n'

export type ApiErrorMessageObject = {
  error: string
}

export interface ApiError {
  response: {
    status: number
    data: ApiErrorMessageObject | string
  }
}

/**
 * Type Guard: Checks if the error matches our API error structure.
 * Use this function to validate BEFORE extracting error details.
 */
export const isApiError = (error: unknown): error is ApiError => {
  // 1. Check if 'error' is a non-null object.
  if (typeof error !== 'object' || error === null) {
    return false
  }

  // 2. Check if 'error' has a 'response' property.
  if (!('response' in error)) {
    return false
  }

  // 3. Check if 'response' is a non-null object.
  const response = (error as { response: unknown }).response
  if (typeof response !== 'object' || response === null) {
    return false
  }

  // 4. Check if 'response' has 'status' (being a number) and 'data' properties.
  if (
    !('status' in response) ||
    typeof (response as { status: unknown }).status !== 'number' ||
    !('data' in response)
  ) {
    return false
  }

  // 5. Check the type of the 'data' property.
  const data = (response as { data: unknown }).data

  // 5a. If 'data' is a string, it's a valid ApiError.
  if (typeof data === 'string') {
    return true
  }

  // 5b. If 'data' is an object, check if it has an 'error' property which is a string.
  if (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as { error: unknown }).error === 'string'
  ) {
    return true
  }

  // If 'data' is neither a string nor a valid error object, it's not a match.
  return false
}

/**
 * Extracts an error message from a validated ApiError object.
 * Use this function AFTER validating the error with `isApiError`.
 */
export const getApiErrorMessage = (error: ApiError): string => {
  const { data } = error.response
  const defaultMessage = t9n('formValidation.serverUnknownError')

  if (typeof data === 'string') {
    return data.length > 0 ? data : defaultMessage
  }

  // If 'data' is an object, the 'error' property is guaranteed by the type guard.
  if (data.error && data.error.length > 0) {
    return data.error
  }

  return defaultMessage
}

/**
 * Extracts an error message from various error types (Error, string, object).
 * Serves as a fallback when the error is not a specific API error.
 */
export const getGenericErrorMessage = (error: unknown): string => {
  const defaultMessage = t9n('formValidation.serverUnknownError')

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string' && error.length > 0) {
    return error
  }

  if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
    return (error as { message: string }).message
  }

  return defaultMessage
}
